/**
 * CSRF Protection Module
 * Provides protection against Cross-Site Request Forgery attacks
 */

/**
 * CSRF token storage and management
 */
class CSRFTokenManager {
  constructor() {
    this.token = null;
    this.tokenExpiry = null;
    this.tokenDuration = 60 * 60 * 1000; // 1 hour
  }

  /**
   * Generate a cryptographically secure random token
   */
  generateToken() {
    // Use crypto.getRandomValues for secure random generation
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);

    // Convert to base64 for URL safety
    const token = btoa(String.fromCharCode.apply(null, array))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');

    this.token = token;
    this.tokenExpiry = Date.now() + this.tokenDuration;

    return token;
  }

  /**
   * Get current valid token or generate new one
   */
  getToken() {
    if (!this.token || Date.now() >= this.tokenExpiry) {
      return this.generateToken();
    }
    return this.token;
  }

  /**
   * Validate token
   */
  validateToken(token) {
    if (!token || !this.token) return false;
    if (Date.now() >= this.tokenExpiry) return false;

    // Constant-time comparison to prevent timing attacks
    return this.constantTimeEquals(token, this.token);
  }

  /**
   * Constant-time string comparison
   */
  constantTimeEquals(a, b) {
    if (a.length !== b.length) return false;

    let result = 0;
    for (let i = 0; i < a.length; i++) {
      result |= a.charCodeAt(i) ^ b.charCodeAt(i);
    }
    return result === 0;
  }

  /**
   * Clear token
   */
  clearToken() {
    this.token = null;
    this.tokenExpiry = null;
  }
}

// Global token manager instance
const tokenManager = new CSRFTokenManager();

/**
 * Add CSRF token to request headers
 */
export function addCSRFToken(headers = {}) {
  const token = tokenManager.getToken();

  return {
    ...headers,
    'X-CSRF-Token': token,
    'X-Request-ID': generateRequestId()
  };
}

/**
 * Generate unique request ID for tracking
 */
function generateRequestId() {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2);
  return `${timestamp}-${random}`;
}

/**
 * Validate CSRF token from response or headers
 */
export function validateCSRFResponse(response) {
  const responseToken = response.headers?.get('X-CSRF-Token');
  if (responseToken) {
    // Update our token if server provides a new one
    tokenManager.token = responseToken;
    tokenManager.tokenExpiry = Date.now() + tokenManager.tokenDuration;
  }
  return true;
}

/**
 * Get CSRF token for manual use
 */
export function getCSRFToken() {
  return tokenManager.getToken();
}

/**
 * Check if request should include CSRF protection
 */
export function shouldIncludeCSRF(method, endpoint) {
  // Only include CSRF for state-changing methods
  const stateMethods = ['POST', 'PUT', 'PATCH', 'DELETE'];

  // Skip CSRF for certain public read-only endpoints
  const publicEndpoints = ['/health', '/status', '/public'];
  const isPublicEndpoint = publicEndpoints.some(path => endpoint.includes(path));

  return stateMethods.includes(method.toUpperCase()) && !isPublicEndpoint;
}

/**
 * Additional request origin validation
 */
export function validateRequestOrigin(allowedOrigins = []) {
  if (typeof window === 'undefined') return true; // Server-side

  const currentOrigin = window.location.origin;

  // If no allowed origins specified, allow same-origin
  if (allowedOrigins.length === 0) {
    return true; // Same-origin requests are allowed by default
  }

  return allowedOrigins.includes(currentOrigin);
}

/**
 * Create CSRF protection middleware
 */
export function createCSRFMiddleware(options = {}) {
  const {
    enabled = true,
    allowedOrigins = [],
    tokenHeader = 'X-CSRF-Token',
    skipEndpoints = ['/health', '/status']
  } = options;

  return {
    beforeRequest: (method, endpoint, requestOptions = {}) => {
      if (!enabled) return requestOptions;

      // Validate origin
      if (!validateRequestOrigin(allowedOrigins)) {
        throw new Error('Request origin not allowed');
      }

      // Add CSRF token if needed
      if (shouldIncludeCSRF(method, endpoint) && !skipEndpoints.some(skip => endpoint.includes(skip))) {
        const headers = addCSRFToken(requestOptions.headers);
        return { ...requestOptions, headers };
      }

      return requestOptions;
    },

    afterResponse: (response, data) => {
      if (!enabled) return data;

      // Validate CSRF response
      validateCSRFResponse(response);
      return data;
    }
  };
}

/**
 * SameSite cookie configuration helper
 */
export function configureSameSiteCookies() {
  // This would typically be configured on the server side,
  // but we can provide guidance for client-side cookie handling

  const cookieConfig = {
    sameSite: 'Strict', // Prevent CSRF via cookies
    secure: window.location.protocol === 'https:', // Only send over HTTPS in production
    httpOnly: false, // Allow JavaScript access (for client-side tokens)
    maxAge: 3600 // 1 hour
  };

  return cookieConfig;
}

/**
 * Double Submit Cookie pattern implementation
 */
export class DoubleSubmitCSRF {
  constructor(cookieName = 'csrf-token') {
    this.cookieName = cookieName;
  }

  /**
   * Set CSRF token in cookie and return for header
   */
  setupDoubleSubmit() {
    const token = tokenManager.getToken();

    // Set cookie (if possible in current context)
    if (typeof document !== 'undefined') {
      const cookieConfig = configureSameSiteCookies();
      document.cookie = `${this.cookieName}=${token}; SameSite=${cookieConfig.sameSite}; Secure=${cookieConfig.secure}; Max-Age=${cookieConfig.maxAge}`;
    }

    return token;
  }

  /**
   * Validate double submit tokens
   */
  validateDoubleSubmit(headerToken) {
    if (typeof document === 'undefined') return true;

    const cookies = document.cookie.split(';');
    const csrfCookie = cookies.find(cookie =>
      cookie.trim().startsWith(`${this.cookieName}=`)
    );

    if (!csrfCookie) return false;

    const cookieToken = csrfCookie.split('=')[1];
    return tokenManager.constantTimeEquals(headerToken, cookieToken);
  }
}

// Export default instance
export const doubleSubmitCSRF = new DoubleSubmitCSRF();